import React from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, CalendarDays, Receipt, Package, Settings, LogOut, Dumbbell, Eye, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import NeedsOutcomePrompt from "./NeedsOutcomePrompt";
import { ReadOnlyBanner } from "./ReadOnlyBanner";

const ADMIN_EMAIL = "dvdquintana32@gmail.com";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, testid: "nav-schedule" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices" },
  { to: "/packages", label: "Packages", icon: Package, testid: "nav-packages" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();

  const isAdmin =
    user?.is_admin === true ||
    (user?.email && user.email.trim().toLowerCase() === ADMIN_EMAIL);
  const visibleNavItems = isAdmin
    ? [...navItems, { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" }]
    : navItems;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleStopImpersonation = async () => {
    try {
      await api.post("/admin/stop-impersonation");
      toast.success("Returned to admin account");
      await checkAuth();
      navigate("/admin", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to stop impersonation");
    }
  };

  return (
    <div className={`min-h-screen flex bg-[#FAF7F2] text-[#2A2623] ${user?.impersonating ? "pt-10" : ""}`}>
      {user?.impersonating && (
        <div
          className="fixed top-0 inset-x-0 z-50 bg-[#0F6B6B] text-[#FFFFFF] px-4 py-2 flex items-center justify-between gap-3 shadow-lg"
          data-testid="impersonation-banner"
        >
          <div className="flex items-center gap-2 min-w-0 text-xs sm:text-sm font-bold uppercase tracking-wider">
            <Eye className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              Viewing as {user.name || user.email}
              {user.impersonator_email ? ` · admin: ${user.impersonator_email}` : ""}
            </span>
          </div>
          <Button
            size="sm"
            onClick={handleStopImpersonation}
            data-testid="stop-impersonation-btn"
            className="bg-[#FAF7F2] text-[#0F6B6B] hover:bg-[#FAF7F2]/90 h-7 px-3 text-xs font-bold uppercase tracking-wider"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Exit
          </Button>
        </div>
      )}
      {/* Sidebar — desktop */}
      <aside className="hidden md:flex w-64 flex-col border-r tm-divider bg-[#FAF7F2] fixed inset-y-0 left-0 z-30" data-testid="sidebar">
        <div className="px-6 py-6 border-b tm-divider flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[var(--logo)] flex items-center justify-center">
            <Dumbbell className="w-5 h-5 text-[var(--logo-fg)]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-xl font-black tracking-tight uppercase">TrainMate</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6B635C]">AU Edition</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {visibleNavItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[#EAF2F2] text-[#0F6B6B] border-l-2 border-[#0F6B6B] pl-[10px]"
                    : "text-[#2A2623] hover:text-[#2A2623] hover:bg-[#EEE9E0]"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              <span className="tracking-wide">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t tm-divider">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-[#E8E1D6]" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#E8E1D6] flex items-center justify-center text-sm font-bold">
                {(user?.name || "U")[0].toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-[#6B635C] truncate">{user?.email}</div>
            </div>
          </div>
          {user?.trial && !user.trial.exempt && user.trial.active && (
            <Link
              to="/settings#plan"
              data-testid="trial-countdown"
              className="block mt-1 mb-2 px-3 py-2 rounded-md bg-[#0F6B6B]/[0.08] border border-[#0F6B6B]/30 text-[11px] font-semibold uppercase tracking-wider text-[#0F6B6B] hover:bg-[#0F6B6B]/[0.14] transition-colors"
            >
              Trial · {user.trial.days_left} day{user.trial.days_left === 1 ? "" : "s"} left
            </Link>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start text-[#2A2623] hover:text-[#2A2623]"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main content — min-w-0 lets this flex item shrink to the viewport so
          the Schedule grid's own overflow-auto box is the ONLY thing that
          scrolls sideways; without it the grid's min-w-[820px] inner forces
          the whole page wider than the phone and the page scrolls horizontally
          (hiding the hour column, Monday and the title). */}
      <main className="flex-1 min-w-0 md:ml-64 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0">
        <header className="md:hidden sticky top-0 z-20 px-4 py-3 border-b tm-divider bg-[#FAF7F2]/95 backdrop-blur flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded bg-[var(--logo)] flex items-center justify-center shrink-0">
              <Dumbbell className="w-4 h-4 text-[var(--logo-fg)]" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-black uppercase tracking-tight truncate">TrainMate</span>
          </div>
          {user?.trial && !user.trial.exempt && user.trial.active && (
            <Link
              to="/settings#plan"
              data-testid="trial-countdown-mobile"
              className="shrink-0 px-2.5 py-1 rounded-md bg-[#0F6B6B]/[0.08] border border-[#0F6B6B]/30 text-[10px] font-semibold uppercase tracking-wider text-[#0F6B6B] hover:bg-[#0F6B6B]/[0.14] transition-colors whitespace-nowrap"
            >
              {user.trial.days_left} day{user.trial.days_left === 1 ? "" : "s"} left
            </Link>
          )}
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button-mobile" className="shrink-0">
            <LogOut className="w-4 h-4" />
          </Button>
        </header>

        <div className="px-4 sm:px-6 lg:px-10 py-6 md:py-8 max-w-7xl mx-auto">
          <ReadOnlyBanner />
          <Outlet />
        </div>
      </main>
      <NeedsOutcomePrompt />

      {/* Bottom tab bar — mobile. Safe-area padding keeps it clear of the
          iPhone home indicator so every item stays tappable. */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[#FAF7F2] border-t tm-divider pb-[env(safe-area-inset-bottom)]"
        data-testid="bottom-nav"
      >
        <div className="grid grid-cols-6 px-2">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 min-h-[44px] py-2 px-0.5 text-[9px] font-semibold uppercase tracking-tight leading-none transition-colors ${
                  isActive ? "text-[#0F6B6B]" : "text-[#6B635C]"
                }`
              }
            >
              <Icon className="w-5 h-5 shrink-0" strokeWidth={2} />
              <span className="w-full text-center truncate">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
